import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type MemberProfile } from "../api";
import { useAuth } from "../auth";
import { Avatar } from "./Avatar";

interface Props {
  bookId: number;
  picker: MemberProfile | undefined;
  members: MemberProfile[];
}

// "Picked by NAME" with an admin-only inline dropdown to fix attribution.
// Anyone sees the static link; admins see a small caret that opens a
// member-list popover and updates the book's added_by_user_id on click.
export function PickerEditor({ bookId, picker, members }: Props) {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const update = useMutation({
    mutationFn: (userId: number) => api.patch(`/books/${bookId}/picker`, { userId }),
    onSuccess: () => {
      setOpen(false);
      // Refresh anywhere the picker is shown.
      qc.invalidateQueries({ queryKey: ["book", bookId] });
      qc.invalidateQueries({ queryKey: ["books"] });
      qc.invalidateQueries({ queryKey: ["current-book"] });
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });

  const display = picker ? (
    <Link
      to={`/members/${picker.id}`}
      className="inline-flex items-center gap-2.5 text-sm text-plum-soft hover:text-rose"
    >
      <Avatar name={picker.name} email={picker.email} hue={picker.avatarHue} size="sm" />
      <span>
        Picked by{" "}
        <span className="text-plum font-medium">
          {picker.name ?? picker.email.split("@")[0]}
        </span>
      </span>
    </Link>
  ) : (
    <span className="inline-flex items-center gap-2.5 text-sm text-plum-mute italic">
      Picker not set
    </span>
  );

  if (!isAdmin) return display;

  return (
    <div ref={wrapRef} className="relative inline-flex items-center gap-2">
      {display}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-[9px] uppercase tracking-[0.22em] text-ink-mute hover:text-bordeaux px-2 py-1 border border-rule hover:border-bordeaux transition-colors"
        title="Change who picked this book"
      >
        Change
      </button>
      {open ? (
        <div className="absolute left-0 top-full mt-2 z-30 bg-white border border-rule min-w-[240px] shadow-lift">
          <div className="px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink-mute border-b border-rule">
            Reassign picker
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {members.map((m) => {
              const isCurrent = picker?.id === m.id;
              return (
                <li key={m.id}>
                  <button
                    onClick={() => {
                      if (isCurrent) {
                        setOpen(false);
                        return;
                      }
                      update.mutate(m.id);
                    }}
                    disabled={update.isPending}
                    className={[
                      "w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-colors",
                      isCurrent
                        ? "bg-rose-paper-2/50 text-bordeaux"
                        : "text-ink hover:bg-rose-paper",
                      "disabled:opacity-60",
                    ].join(" ")}
                  >
                    <Avatar name={m.name} email={m.email} hue={m.avatarHue} size="sm" />
                    <span className="flex-1 min-w-0 truncate">
                      {m.name ?? m.email.split("@")[0]}
                    </span>
                    {isCurrent ? (
                      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-bordeaux">
                        current
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
